import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';
import { Search, ChevronDown, ChevronUp, FileText, User, Mail, Phone, Calendar } from 'lucide-react';

const FeedbackTab = ({ setMessage, setError }) => {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        loadFeedback();
    }, []);

    const loadFeedback = async () => {
        try {
            setLoading(true);
            const response = await http.get('/admin/feedback');
            if (response.data.success) {
                setFeedback(response.data.feedback || []);
            }
        } catch (error) {
            console.error('Failed to load feedback:', error);
            setError?.('Failed to load customer feedback');
        } finally {
            setLoading(false);
        }
    };

    const filteredFeedback = feedback.filter(item =>
        item.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.feedback.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
                    <p className="text-gray-600">Loading feedback...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Customer Feedback & Complaints</h2>
                        <p className="text-gray-600 text-sm mt-1">
                            Total: {filteredFeedback.length} feedback{filteredFeedback.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <button
                        onClick={loadFeedback}
                        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-medium transition-colors"
                    >
                        Refresh
                    </button>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by customer name, email, or feedback..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                        />
                    </div>
                </div>

                {/* Feedback List */}
                {filteredFeedback.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                        <p className="text-gray-600 text-lg">No feedback found</p>
                        <p className="text-gray-500 text-sm mt-2">
                            {searchTerm ? 'Try adjusting your search criteria' : 'Customer feedback will appear here'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredFeedback.map((item) => (
                            <div
                                key={item._id}
                                className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                            >
                                {/* Header */}
                                <div
                                    className="bg-gray-50 px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => toggleExpand(item._id)}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <User className="text-orange-500" size={20} />
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {item.customer_name}
                                                </h3>
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                                    {item.plan}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                                <div className="flex items-center gap-1">
                                                    <Mail size={14} />
                                                    <span>{item.customer_email}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Phone size={14} />
                                                    <span>{item.customer_phone}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Calendar size={14} />
                                                    <span>{item.updated_at}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <button className="text-gray-500 hover:text-gray-700">
                                            {expandedId === item._id ? (
                                                <ChevronUp size={24} />
                                            ) : (
                                                <ChevronDown size={24} />
                                            )}
                                        </button>
                                    </div>

                                    {/* Preview (when collapsed) */}
                                    {expandedId !== item._id && (
                                        <div className="mt-3 text-gray-700 text-sm line-clamp-2">
                                            {item.feedback}
                                        </div>
                                    )}
                                </div>

                                {/* Expanded Content */}
                                {expandedId === item._id && (
                                    <div className="bg-white px-6 py-4 border-t border-gray-200">
                                        <div className="space-y-4">
                                            {/* Feedback */}
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Feedback:</h4>
                                                <p className="text-gray-800 whitespace-pre-wrap bg-gray-50 p-4 rounded-md">
                                                    {item.feedback}
                                                </p>
                                            </div>

                                            {/* Additional Info */}
                                            {(item.allergy_info || item.ingredients_to_avoid?.length > 0 || item.food_preferences?.length > 0) && (
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                                                    {item.allergy_info && (
                                                        <div>
                                                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Allergies:</h4>
                                                            <p className="text-sm text-gray-600">{item.allergy_info}</p>
                                                        </div>
                                                    )}
                                                    {item.ingredients_to_avoid?.length > 0 && (
                                                        <div>
                                                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Avoid:</h4>
                                                            <div className="flex flex-wrap gap-1">
                                                                {item.ingredients_to_avoid.map((ing, idx) => (
                                                                    <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                                                        {ing}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {item.food_preferences?.length > 0 && (
                                                        <div>
                                                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Preferences:</h4>
                                                            <div className="flex flex-wrap gap-1">
                                                                {item.food_preferences.map((pref, idx) => (
                                                                    <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                                                        {pref}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeedbackTab;
